import React, { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { Navigate } from 'react-router'
import { api } from '../utils/api'
import {
  UserGroupIcon,
  CheckBadgeIcon,
  ShieldCheckIcon,
  PencilSquareIcon,
  TrashIcon,
  ShieldExclamationIcon,
  KeyIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline'

const AdminUsers = () => {
  const { user, loading: isLoading, isAdmin } = useAuth()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 })
  const [stats, setStats] = useState({ total: 0, verified: 0, admins: 0 })
  const [showModal, setShowModal] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [formData, setFormData] = useState({ name: '', email: '', admin_role: false })
  const [nameFilter, setNameFilter] = useState('')
  const [emailFilter, setEmailFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [showFilters, setShowFilters] = useState(false)

  // Bumping reloadKey refetches after a change
  const [reloadKey, setReloadKey] = useState(0)
  const reload = () => setReloadKey(k => k + 1)

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        let query = `/admin/users?page=${pagination.page}&limit=${pagination.limit}`
        if (nameFilter) query += `&name=${encodeURIComponent(nameFilter)}`
        if (emailFilter) query += `&email=${encodeURIComponent(emailFilter)}`
        if (statusFilter) query += `&status=${encodeURIComponent(statusFilter)}`

        const response = await api.get(query)
        setUsers(response.data.users)
        setPagination(prev => ({
          ...prev,
          ...response.data.pagination
        }))
        if (response.data.stats) {
          setStats(response.data.stats)
        }
      } catch (error) {
        setError('Fout bij ophalen van gebruikers')
        console.error('Fetch users error:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchUsers()
  }, [pagination.page, pagination.limit, nameFilter, emailFilter, statusFilter, reloadKey])

  const handleMakeAdmin = async (userId) => {
    if (!window.confirm('Weet je zeker dat je deze gebruiker admin rechten wilt geven?')) {
      return
    }

    try {
      setError('')
      const response = await api.post(`/admin/users/${userId}/make-admin`)
      setSuccess(response.data.message)
      reload()
    } catch (error) {
      setError(error.response?.data?.error || 'Fout bij toekennen van admin rechten')
    }
  }

  const handleRemoveAdmin = async (userId) => {
    if (!window.confirm('Weet je zeker dat je de admin rechten van deze gebruiker wilt intrekken?')) {
      return
    }

    try {
      setError('')
      const response = await api.delete(`/admin/users/${userId}/admin`)
      setSuccess(response.data.message)
      reload()
    } catch (error) {
      setError(error.response?.data?.error || 'Fout bij intrekken van admin rechten')
    }
  }

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Weet je zeker dat je gebruiker "${userName}" wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.`)) {
      return
    }

    try {
      setError('')
      const response = await api.delete(`/admin/users/${userId}`)
      setSuccess(response.data.message)
      reload()
    } catch (error) {
      setError(error.response?.data?.error || 'Fout bij verwijderen van gebruiker')
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('nl-NL')
  }

  const handleOpenCreateModal = () => {
    setEditingUser(null)
    setFormData({ name: '', email: '', admin_role: false })
    setShowModal(true)
  }

  const handleOpenEditModal = (userItem) => {
    setEditingUser(userItem)
    setFormData({
      name: userItem.name,
      email: userItem.email,
      admin_role: !!userItem.admin_role
    })
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingUser(null)
    setFormData({ name: '', email: '', admin_role: false })
    setError('')
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleFilterChange = (filterType, value) => {
    if (filterType === 'name') {
      setNameFilter(value)
    } else if (filterType === 'email') {
      setEmailFilter(value)
    } else if (filterType === 'status') {
      setStatusFilter(value)
    }
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  const handleLimitChange = (newLimit) => {
    setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }))
  }

  const clearFilters = () => {
    setNameFilter('')
    setEmailFilter('')
    setStatusFilter('')
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    try {
      if (editingUser) {
        // Update user
        const response = await api.put(`/admin/users/${editingUser.id}`, { name: formData.name, email: formData.email })
        setSuccess(response.data.message)
      } else {
        // Create new user
        const response = await api.post('/admin/users', formData)
        setSuccess(response.data.message)
      }

      handleCloseModal()
      reload()
    } catch (error) {
      const details = error.response?.data?.details
      if (Array.isArray(details) && details.length > 0) {
        setError(details.map(detail => detail.message).join('\n'))
      } else {
        setError(error.response?.data?.error || 'Er is een fout opgetreden')
      }
    }
  }

  if (isLoading) {
    return <div className="flex justify-center items-center min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
    </div>
  }

  if (!user || !isAdmin) {
    return <Navigate to="/login" replace />
  }

  return (
    <div className="container mx-auto px-4 py-8 min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Leden Beheer</h1>
        <button
          onClick={handleOpenCreateModal}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          + Nieuw Lid
        </button>
      </div>

      {error && (
        <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-sm mb-4 whitespace-pre-line">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 dark:bg-green-900/30 border border-green-400 dark:border-green-700 text-green-700 dark:text-green-300 px-4 py-3 rounded-sm mb-4">
          {success}
        </div>
      )}

      {/* Statistics Cards */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow-sm rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="shrink-0">
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-md flex items-center justify-center">
                  <UserGroupIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Totaal Leden
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {stats.total}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow-sm rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="shrink-0">
                <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-md flex items-center justify-center">
                  <CheckBadgeIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Geverifieerd
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {stats.verified}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow-sm rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="shrink-0">
                <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-md flex items-center justify-center">
                  <ShieldCheckIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Administrators
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {stats.admins}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Page Size */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mb-4">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="w-full px-4 py-3 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-lg"
        >
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Filters</h2>
          {showFilters ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          )}
        </button>

        {showFilters && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Zoek op naam
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={nameFilter}
                    onChange={(e) => handleFilterChange('name', e.target.value)}
                    placeholder="Naam..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Zoek op email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={emailFilter}
                    onChange={(e) => handleFilterChange('email', e.target.value)}
                    placeholder="Email..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Alle statussen</option>
                  <option value="verified">Geverifieerd</option>
                  <option value="unverified">Niet geverifieerd</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Items per pagina
                </label>
                <div className="flex items-center space-x-2">
                  <select
                    value={pagination.limit}
                    onChange={(e) => handleLimitChange(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  {(nameFilter || emailFilter || statusFilter) && (
                    <button
                      onClick={clearFilters}
                      className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors flex items-center"
                      title="Filters wissen"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="md:hidden space-y-4">
            {users.map((userItem) => (
              <div key={userItem.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                      {userItem.name}
                    </h3>
                    {userItem.admin_role && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                        Admin
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{userItem.email}</p>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-start text-sm">
                    <span className="font-medium text-gray-700 dark:text-gray-300 w-24 shrink-0">Status:</span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      userItem.verified
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                        : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                    }`}>
                      {userItem.verified ? 'Geverifieerd' : 'Niet geverifieerd'}
                    </span>
                  </div>
                  <div className="flex items-start text-sm">
                    <span className="font-medium text-gray-700 dark:text-gray-300 w-24 shrink-0">Reactie:</span>
                    <span className="text-gray-900 dark:text-white">
                      <span className="text-green-600 dark:text-green-400 font-medium cursor-help" title="Komt">
                        {userItem.confirmed_count || 0}
                      </span>
                      {' / '}
                      <span className="text-orange-600 dark:text-orange-400 font-medium cursor-help" title="Niet gereageerd">
                        {userItem.no_response_count || 0}
                      </span>
                      {' / '}
                      <span className="text-red-600 dark:text-red-400 font-medium cursor-help" title="Komt niet">
                        {userItem.declined_count || 0}
                      </span>
                    </span>
                  </div>
                  <div className="flex items-start text-sm">
                    <span className="font-medium text-gray-700 dark:text-gray-300 w-24 shrink-0">Aangemeld:</span>
                    <span className="text-gray-900 dark:text-white">{formatDate(userItem.created_at)}</span>
                  </div>
                </div>

                <div className="flex justify-end space-x-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => handleOpenEditModal(userItem)}
                    className="text-blue-600 hover:text-blue-900 p-2 hover:bg-blue-50 rounded-sm transition-colors"
                    title="Gebruiker bewerken"
                  >
                    <PencilSquareIcon className="h-5 w-5" />
                  </button>

                  {!userItem.admin_role ? (
                    <button
                      onClick={() => handleMakeAdmin(userItem.id)}
                      className="text-green-600 hover:text-green-900 p-2 hover:bg-green-50 rounded-sm transition-colors"
                      title="Admin maken"
                    >
                      <KeyIcon className="h-5 w-5" />
                    </button>
                  ) : (
                    <button
                      onClick={() => handleRemoveAdmin(userItem.id)}
                      className="text-orange-600 hover:text-orange-900 p-2 hover:bg-orange-50 rounded-sm transition-colors disabled:opacity-50"
                      title="Admin rechten intrekken"
                      disabled={userItem.id === user.id}
                    >
                      <ShieldExclamationIcon className="h-5 w-5" />
                    </button>
                  )}

                  {userItem.id !== user.id && (
                    <button
                      onClick={() => handleDeleteUser(userItem.id, userItem.name)}
                      className="text-red-600 hover:text-red-900 p-2 hover:bg-red-50 rounded-sm transition-colors"
                      title="Gebruiker verwijderen"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Tablet/Desktop Table View */}
          <div className="hidden md:block bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Lid
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Reactie
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Aangemeld
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Acties
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {users.map((userItem) => (
                  <tr key={userItem.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                            {userItem.name}
                            {userItem.admin_role && (
                              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                                Admin
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{userItem.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        userItem.verified
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                          : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                      }`}>
                        {userItem.verified ? 'Geverifieerd' : 'Niet geverifieerd'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        <span className="text-green-600 dark:text-green-400 font-medium cursor-help" title="Komt">
                          {userItem.confirmed_count || 0}
                        </span>
                        {' / '}
                        <span className="text-orange-600 dark:text-orange-400 font-medium cursor-help" title="Niet gereageerd">
                          {userItem.no_response_count || 0}
                        </span>
                        {' / '}
                        <span className="text-red-600 dark:text-red-400 font-medium cursor-help" title="Komt niet">
                          {userItem.declined_count || 0}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {formatDate(userItem.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleOpenEditModal(userItem)}
                          className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded-sm transition-colors"
                          title="Gebruiker bewerken"
                        >
                          <PencilSquareIcon className="h-5 w-5" />
                        </button>

                        {!userItem.admin_role ? (
                          <button
                            onClick={() => handleMakeAdmin(userItem.id)}
                            className="text-green-600 hover:text-green-900 p-1 hover:bg-green-50 rounded-sm transition-colors"
                            title="Admin maken"
                          >
                            <KeyIcon className="h-5 w-5" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleRemoveAdmin(userItem.id)}
                            className="text-orange-600 hover:text-orange-900 p-1 hover:bg-orange-50 rounded-sm transition-colors disabled:opacity-50"
                            title="Admin rechten intrekken"
                            disabled={userItem.id === user.id}
                          >
                            <ShieldExclamationIcon className="h-5 w-5" />
                          </button>
                        )}

                        {userItem.id !== user.id && (
                          <button
                            onClick={() => handleDeleteUser(userItem.id, userItem.name)}
                            className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded-sm transition-colors"
                            title="Gebruiker verwijderen"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 mt-4 md:mt-0 rounded-lg md:rounded-none shadow-sm md:shadow-none">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page <= 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                >
                  Vorige
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page >= pagination.pages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                >
                  Volgende
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Pagina <span className="font-medium">{pagination.page}</span> van{' '}
                    <span className="font-medium">{pagination.pages}</span> -{' '}
                    <span className="font-medium">{pagination.total}</span> leden totaal
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-xs -space-x-px">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={pagination.page <= 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                    >
                      Vorige
                    </button>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={pagination.page >= pagination.pages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                    >
                      Volgende
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
                {editingUser ? 'Lid Bewerken' : 'Nieuw Lid'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-4">
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Naam
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>

                {!editingUser && (
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="admin_role"
                      name="admin_role"
                      checked={formData.admin_role}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded-sm"
                    />
                    <label htmlFor="admin_role" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                      Admin rechten toekennen
                    </label>
                  </div>
                )}

                {!editingUser && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formData.admin_role
                      ? 'De admin logt in met een code per e-mail; er is geen wachtwoord nodig.'
                      : 'De gebruiker ontvangt een verificatie-e-mail en logt daarna in met een code per e-mail.'}
                  </p>
                )}
              </div>

              {error && (
                <div className="mt-4 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-sm whitespace-pre-line">
                  {error}
                </div>
              )}

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Annuleren
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingUser ? 'Opslaan' : 'Aanmaken'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminUsers